import moment from "moment";
import { isUnlimitedLimit } from "helpers/methods";
import { isPerSeatPlan } from "helpers/perSeatHelpers";
import { PlanSwitchPreview } from "types/perSeat";
import { getCardLogo, getCardPlate } from "./MyPlanHelper";
import { getPerSeatMonthly } from "./UpgradePlanHelper";

/**
 * Pure view-model logic for Settings → My Plan → Checkout.
 *
 * Two different billing rules meet on this screen:
 *
 * 1. **Switching plan is anchor-reset:** the new plan is always charged in
 *    full for a fresh billing cycle starting today, and unused time on the
 *    outgoing plan (when there is any to credit) is applied against that —
 *    see `PlanSwitchPreview`. `GET /billing/fetch/plan/{planId}/switch/preview`
 *    (`fetchPlanSwitchPreview`) returns the authoritative numbers for every
 *    "switch" checkout, first purchase included (it answers with
 *    `prorated: false` and no credit line when there's nothing to switch
 *    from); pass its response as `preview` and this file builds the summary
 *    from it. Without one — e.g. while it's still loading — this computes the
 *    same shape client-side as an estimate.
 * 2. **Adding a seat mid-cycle is prorated differently**, and that figure is
 *    never computed here: `GET /billing/fetch/seat/preview` returns the exact
 *    amount and the add-user / reactivate dialogs show it verbatim.
 */

/** Whether checkout was opened to switch plan or only to change seat count. */
export type CheckoutType = "switch" | "seats";

/** One row of the order, mirroring a Stripe invoice line. */
export interface CheckoutLine {
  label: string;
  /** Formatted and signed, e.g. "$75.00" or "−$10.00". */
  value: string;
}

export interface CheckoutSummary {
  title: string;
  /** One line under the title explaining how this order is billed. */
  subtitle: string;
  planLine: string;
  /** Recurring charge, e.g. "$70.00". */
  monthly: string;
  showAnnualNote: boolean;
  annualNote: string;
  /**
   * The invoice's line items — the new plan's full charge for a fresh cycle,
   * then (when prorated) the credit for unused time on the old one.
   */
  lines: CheckoutLine[];
  /** Sum of the lines, before any account balance is spent. */
  total: string;
  /** Whether saved account credit covers part of this invoice. */
  showAppliedBalance: boolean;
  /** Balance spent on this invoice, as a negative amount. */
  appliedBalance: string;
  /** Set when credit remains after this invoice, or carries forward. */
  creditNote: string;
  /** What the card is actually charged: total less the applied balance. */
  due: string;
  renewNote: string;
  payLabel: string;
}

export interface CheckoutSummaryInput {
  type: CheckoutType;
  /** Plan being purchased, from the plans API. */
  plan: any;
  /** Plan the account is on today — supplies the billing-cycle length. */
  currentPlan: any;
  isAnnual: boolean;
  /** Seats being sold — the subscription quantity, and what gets billed. */
  seats: number;
  /**
   * Seats the account pays for today, for the credit on the outgoing plan.
   * Defaults to `seats` (a first purchase has nothing to credit anyway).
   */
  currentSeats?: number;
  /** Whether the plan multiplies its price by the seat count (§7). */
  isPerSeat: boolean;
  /** End of the current billing period (`accountStats.validTill`). */
  validTill?: string;
  /** Whole-percent saving the annual plans advertise. */
  annualSavingsPercent: number;
  /**
   * Whether a paid subscription already exists. Without one this is a first
   * purchase: the backend creates the subscription and Stripe invoices the
   * whole first period up front, so there is nothing to prorate or credit.
   */
  hasActiveSubscription?: boolean;
  /**
   * Credit saved on the account (`BillingSummary.creditBalance`). Stripe spends
   * it before charging the card, which is what turns a real total into a `$0.00`
   * payment — the invoice calls it the applied balance.
   */
  accountCredit?: number;
  /**
   * An offer schedule is in play on the account (`BillingConfig.offerEnabled`).
   * `PaymentService.updateSubscriptionPlan` excludes these accounts from
   * proration regardless of the per-seat/subscription state — see `isProrated`.
   */
  offerEnabled?: boolean;
  /**
   * The live `GET .../plan/{planId}/switch/preview` response. When present,
   * its figures replace the client-side estimate below for credit, applied
   * balance, and whether the switch is prorated. **Except on an annual
   * switch**: `chargeForNewPlan` / `total` / `amountDueNow` / `nextRenewalAmount`
   * come back priced off the plan's undiscounted monthly rate with no ×12 (a
   * known backend bug — see `trustPreviewCharge` below), so those four always
   * use the locally computed, discounted total instead, preview or not. Omit
   * `preview` only while the call is still in flight (or failed) to fall back
   * to the estimate everywhere.
   */
  preview?: PlanSwitchPreview;
  /**
   * Seasonal sale on this plan (`getPlanSale`). The preview never includes the
   * promotion code, so while one applies the charge is the sale price computed
   * here, and an annual sale renews at the regular price.
   */
  sale?: { unit: number; regular: number } | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** Fallback cycle length when the plan carries no validity. */
const DEFAULT_CYCLE_DAYS = 30;

/** Signed currency, so a negative total reads "−$15.00" and not "$-15.00". */
const money = (value: number) =>
  value < 0 ? `−$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`;

const seatWord = (seats: number) => (seats === 1 ? "seat" : "seats");

/** Days left on the current subscription, clamped to the cycle length. */
export const getDaysLeftInCycle = (validTill?: string, cycleDays = 30) => {
  if (!validTill) return 0;
  const end = new Date(validTill).getTime();
  if (Number.isNaN(end)) return 0;
  const days = Math.ceil((end - Date.now()) / DAY_MS);
  return Math.min(Math.max(days, 0), cycleDays);
};

/** Whether a plan bills once a year; anything else is treated as monthly. */
const isAnnualPlan = (plan: any): boolean => {
  const days = Number(plan?.validityInDays) || 0;
  return days === 365 || days === 366;
};

/**
 * Builds every line of the checkout summary.
 *
 * The switch is **anchor-reset**: the new plan is always charged in full for
 * a fresh billing cycle starting today, whether or not the switch is
 * prorated — proration only decides whether there's a credit line for unused
 * time on the outgoing plan, it never reduces the new plan's own charge.
 * That's gated the same way the backend gates it (`PaymentService
 * .updateSubscriptionPlan`): per-seat, an active subscription, and no offer
 * schedule on the account (`isProrated`).
 *
 * When `preview` (the live `switch/preview` response) is supplied, its
 * credit/proration figures are authoritative. Its charge/total/due/renewal
 * *amount* fields are authoritative for a monthly switch only — on an annual
 * one the endpoint returns those undiscounted and un-annualized, so this
 * always falls back to the locally computed, discounted total for them
 * (`trustPreviewCharge`). Without a `preview` at all, this computes the same
 * shape client-side as an estimate: still anchor-reset for the charge and
 * renewal date, but the credit for unused time is a day-based approximation
 * rather than Stripe's own number.
 */
export const buildCheckoutSummary = ({
  type,
  plan,
  currentPlan,
  isAnnual,
  seats,
  currentSeats,
  isPerSeat,
  validTill,
  annualSavingsPercent,
  hasActiveSubscription = true,
  accountCredit = 0,
  offerEnabled = false,
  preview,
  sale = null,
}: CheckoutSummaryInput): CheckoutSummary => {
  const planName = plan?.name ?? preview?.planName ?? "";
  const unitPrice = sale ? sale.unit : getPerSeatMonthly(plan, isAnnual);
  // Only a per-seat plan multiplies by the seat count; anything else bills flat
  // at quantity 1 however many users the account has. `preview.seats` is
  // already clamped to the plan's cap — prefer it once it's known.
  const billedSeats = isPerSeat ? Math.max(1, preview?.seats ?? seats) : 1;
  const monthlyTotal = unitPrice * billedSeats;
  const yearlyTotal = monthlyTotal * 12;

  // Proration is a per-seat behaviour only, and only when the backend actually
  // updates the existing subscription item in place (`PaymentService
  // .updateSubscriptionPlan`, gated on per-seat + an active subscription + no
  // offer schedule on the account). Every other plan change takes the legacy
  // cancel-and-recreate path — nothing is prorated and no unused time is
  // credited, so no credit line may be shown. The live preview reports this
  // directly once it's loaded.
  const isProrated =
    preview?.prorated ?? (isPerSeat && hasActiveSubscription && !offerEnabled);

  // Anchor-reset: the new plan's own charge is always the full price of a
  // fresh cycle, prorated or not.
  const newCycleTotal = isAnnual ? yearlyTotal : monthlyTotal;
  // `switch/preview`'s dollar fields come back priced off the plan's
  // undiscounted monthly rate with no ×12 on an annual switch — the same
  // class of bug already documented on `AddOnInvoicePreview.nextRenewalAmount`
  // (see the file header). Monthly switches are unaffected, so only annual
  // ones fall back to the locally computed, discounted total.
  // Nor does the preview know about a seasonal sale code — it quotes the
  // undiscounted price — so a sale order is always priced locally too.
  const trustPreviewCharge = !isAnnual && !sale;
  const charge =
    trustPreviewCharge && preview ? preview.chargeForNewPlan : newCycleTotal;

  // Credit for what the account already paid for and won't use on the
  // outgoing plan, estimated day-based when there's no live preview yet. The
  // outgoing plan bills at its own quantity: seats only if it is itself
  // per-seat (§7). A trial or lapsed account has paid for nothing, so there's
  // no credit to estimate.
  const currentDays = Number(currentPlan?.validityInDays) || DEFAULT_CYCLE_DAYS;
  const cycleDays = currentDays > 0 ? currentDays : DEFAULT_CYCLE_DAYS;
  const daysLeft = getDaysLeftInCycle(validTill, cycleDays);
  const factor = cycleDays > 0 ? daysLeft / cycleDays : 0;
  const currentUnit = getPerSeatMonthly(currentPlan, isAnnualPlan(currentPlan));
  const currentQuantity = isPerSeatPlan(currentPlan)
    ? Math.max(1, currentSeats ?? seats)
    : 1;
  // An uncapped plan reports a sentinel seat limit, not a real quantity —
  // pricing it would invent a six-figure credit. Say nothing rather than that,
  // and let the live preview (or Stripe, at switch time) report the real one.
  const canEstimateCredit = !isUnlimitedLimit(currentQuantity);
  const currentCycleTotal =
    (isAnnualPlan(currentPlan) ? currentUnit * 12 : currentUnit) *
    currentQuantity;
  const credit =
    preview?.creditForUnusedTime ??
    (isProrated && canEstimateCredit ? currentCycleTotal * factor : 0);

  // Stripe's own arithmetic, in its order: the lines make a total, saved
  // balance is spent against that total, and only the remainder hits the card.
  //
  // The total is deliberately left signed. When the credit for the outgoing
  // plan is worth more than the new one costs, Stripe invoices a negative
  // total and moves it onto the customer balance — clamping it here would
  // print lines that visibly don't add up.
  const total =
    trustPreviewCharge && preview ? preview.total : charge - credit;
  const creditBalance = preview?.creditBalance ?? accountCredit;
  const appliedBalance =
    trustPreviewCharge && preview
      ? preview.creditApplied
      : Math.min(Math.max(0, accountCredit), Math.max(0, total));
  const due =
    trustPreviewCharge && preview
      ? preview.amountDueNow
      : Math.max(0, total - appliedBalance);
  // Credit that outlives this invoice: an overshoot on this switch plus
  // whatever of the saved balance this invoice didn't need.
  const carriedCredit =
    Math.max(0, -total) + Math.max(0, creditBalance - appliedBalance);

  /** Stripe dates its proration lines from the day of the change. */
  const changeDate = moment().format("D MMM YYYY");

  // Anchor-reset: the next full charge always lands one cycle from today,
  // prorated or not — it never stays at the outgoing plan's own renewal date.
  const renewalDate = preview?.nextRenewalDate
    ? moment(preview.nextRenewalDate)
    : moment().add(1, isAnnual ? "year" : "month");
  const renewalLabel = renewalDate.isValid()
    ? renewalDate.format("D MMM YYYY")
    : "your next renewal";
  // A monthly sale still covers the next renewal (it runs three months); an
  // annual one is over by then, so the year after renews at the regular price.
  const renewalAmount =
    trustPreviewCharge && preview
      ? preview.nextRenewalAmount
      : sale && isAnnual
        ? sale.regular * 12 * billedSeats
        : newCycleTotal;

  return {
    title:
      type === "seats"
        ? `Update to ${billedSeats} ${seatWord(billedSeats)}`
        : `Switch to ${planName}${isAnnual ? " (Annual)" : ""}`,
    subtitle: isProrated
      ? "Review your order — your new plan starts today for a fresh billing cycle, and unused time on your current plan is credited toward it."
      : hasActiveSubscription
        ? `Review your order — your new plan starts today and the first ${
            isAnnual ? "year" : "month"
          } is charged in full.`
        : `Review your order — the first ${
            isAnnual ? "year" : "month"
          } is billed up front${
            isPerSeat
              ? ` for the ${billedSeats} ${seatWord(billedSeats)} you're buying`
              : ""
          }.`,
    // For annual, show the actual discounted price per seat *per year* (what
    // the seat is billed at) rather than its monthly-equivalent rate — that's
    // the number the switch line item and total below are built from.
    planLine: isPerSeat
      ? `${planName} — ${billedSeats} ${seatWord(billedSeats)} × ${money(
          isAnnual ? unitPrice * 12 : unitPrice,
        )}${isAnnual ? "/yr · annual billing" : ""}`
      : `${planName}${isAnnual ? " · annual billing" : ""}`,
    monthly: money(monthlyTotal),
    showAnnualNote: isAnnual,
    annualNote: `You will be billed ${money(
      yearlyTotal,
    )} once per year (${annualSavingsPercent}% off monthly pricing).`,
    // The new plan's own charge — always the full price of a fresh cycle
    // starting today — then, only when the switch is prorated, the credit for
    // unused time on the outgoing plan.
    lines: [
      {
        label: `${planName}${
          hasActiveSubscription ? "" : " — first billing period"
        }, ${isAnnual ? "one year" : "one month"} from ${changeDate}`,
        value: money(charge),
      },
      ...(credit > 0
        ? [
            {
              label: `Unused time on ${
                currentPlan?.name ?? "your current plan"
              } after ${changeDate}`,
              value: `−${money(credit)}`,
            },
          ]
        : []),
    ],
    total: money(total),
    showAppliedBalance: appliedBalance > 0,
    appliedBalance: `−${money(appliedBalance)}`,
    creditNote:
      !preview && isProrated && !canEstimateCredit
        ? "Unused time on your current plan is credited by Stripe when the switch is made."
        : total < 0
          ? // The switch is worth more than it costs, so nothing is charged and
            // the difference joins whatever credit is already saved.
            `${money(
              -total,
            )} is added to your account credit, leaving ${money(
              carriedCredit,
            )} for future invoices.`
          : carriedCredit > 0
            ? `${money(
                carriedCredit,
              )} of credit stays on your account for future invoices.`
            : "",
    due: money(due),
    renewNote: `Starting ${renewalLabel}, you will be charged ${money(
      renewalAmount,
    )} / ${isAnnual ? "year" : "month"}${
      isPerSeat ? ` for ${billedSeats} ${seatWord(billedSeats)}` : ""
    }.${
      !preview && isProrated
        ? " Amounts shown are an estimate — Stripe calculates the exact prorated charge when the switch is made."
        : ""
    }`,
    payLabel: due > 0 ? `Pay ${money(due)}` : "Confirm switch",
  };
};

export interface CheckoutCard {
  id: string;
  /** Raw brand id from Stripe, used for the logo's alt text. */
  network: string;
  /** Network logo asset; undefined for brands with no artwork. */
  logo?: string;
  /** Short plate label ("DISC", "UNION") — the fallback when `logo` is absent. */
  brand: string;
  brandColor: string;
  last4: string;
  isPrimary: boolean;
  isExpired: boolean;
}

/** A saved card is unusable once its expiry day has fully passed. */
export const isCardExpired = (expirationDate?: string) => {
  if (!expirationDate) return false;
  const expiry = new Date(expirationDate);
  if (Number.isNaN(expiry.getTime())) return false;
  expiry.setHours(23, 59, 59, 999);
  return new Date() > expiry;
};

/** Saved cards from the API, shaped for the payment column. */
export const toCheckoutCards = (cards: any[]): CheckoutCard[] =>
  (cards ?? []).map((card: any) => {
    const plate = getCardPlate(card?.brand);
    return {
      id: card?.id,
      network: card?.brand ?? "",
      logo: getCardLogo(card?.brand),
      brand: plate.label,
      brandColor: plate.color,
      last4: String(card?.number ?? ""),
      isPrimary: Boolean(card?.default),
      isExpired: isCardExpired(card?.expirationDate),
    };
  });

/** Masked card line the design renders next to the brand plate. */
export const formatMaskedCard = (last4: string) => `···· ${last4}`;
